import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { uploadFile } from 'api'
import {
  fetchPreschoolCentres,
  fetchTeacherWeeklyBundle,
  saveAdminEvaluationPhoto,
  upsertTeacherWeeklyEvaluation
} from 'api/preschoolEvaluation'
import { uploadPresets } from 'app/config'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

import {
  EVALUATION_OPTIONS,
  NUMERIC_FIXED_GOAL,
  NUMERIC_FIXED_GOAL_ID,
  PRESCHOOL_LEVEL_OPTIONS,
  SEQUENTIAL_FIXED_GOALS,
  sequentialGoalId,
  sequentialStrandLabel
} from '../constants'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  spacer: { flexGrow: 1 },
  weekInfo: { color: theme.palette.text.secondary },
  card: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: theme.spacing(1)
  },
  skillName: {
    fontWeight: 700,
    fontSize: 15,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5)
  },
  goalText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  helpText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginBottom: theme.spacing(1)
  },
  goalBlock: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  yesNo: { minWidth: 110 },
  submittedBadge: { color: '#1B7A3D', fontWeight: 600 },
  photoStrip: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  photoThumb: { position: 'relative', width: 64, height: 64 },
  photoImg: {
    width: 64,
    height: 64,
    objectFit: 'cover',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    display: 'block'
  },
  photoRemove: {
    position: 'absolute',
    top: -8,
    right: -8,
    padding: 2,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': { background: theme.palette.background.paper }
  }
}))

// Photos on one observation — thumbnails (click to open) + add/remove. Persists
// immediately via the admin photos-only endpoint (which also mirrors the
// parent-facing snapshot), independent of the Save/Submit button. onAdd/onRemove
// are already bound to this observation's goal_id by the caller.
const PhotoStrip = ({ imageUrls, onAdd, onRemove, disabled }) => {
  const classes = useStyles()
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const imgs = imageUrls || []

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = '' // allow re-picking the same file
    if (!file) return
    setBusy(true)
    try {
      await onAdd(file)
    } catch (err) {
      showToast(
        err?.response?.status === 404
          ? 'Save this evaluation first, then add photos'
          : 'Photo upload failed',
        'danger'
      )
    } finally {
      setBusy(false)
    }
  }

  const rm = async (url) => {
    setBusy(true)
    try {
      await onRemove(url)
    } catch (err) {
      showToast('Could not remove the photo', 'danger')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box className={classes.photoStrip}>
      {imgs.map((url) => (
        <Box key={url} className={classes.photoThumb}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt="observation" className={classes.photoImg} />
          </a>
          {!disabled && (
            <IconButton
              size="small"
              className={classes.photoRemove}
              onClick={() => rm(url)}
              disabled={busy}
            >
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      ))}
      {!disabled && (
        <>
          <Button
            size="small"
            variant="outlined"
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            {busy ? 'Uploading…' : '+ Photo'}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onFile}
          />
        </>
      )}
    </Box>
  )
}

// Monday of the week containing `dateStr`, plus the month key.
const computeWeek = (dateStr) => {
  const d = dayjs(dateStr)
  if (!d.isValid()) return { weekStart: '', monthKey: '' }
  const dow = d.day()
  const diff = dow === 0 ? -6 : 1 - dow
  const monday = d.add(diff, 'day')
  return {
    weekStart: monday.format('YYYY-MM-DD'),
    monthKey: monday.format('YYYY-MM')
  }
}

const seedForm = (bundle, studentUuid) => {
  const existing = bundle?.existing_evaluations?.[studentUuid] || null
  const plan = bundle?.plan || null
  const skillsDict = bundle?.skills || {}
  const sequentialSkills = Object.values(skillsDict).filter(
    (s) => s && s.type === 'sequential'
  )

  const skillNameById = (id) => (skillsDict[id] && skillsDict[id].name) || ''

  let spiral
  if (existing && existing.spiral && existing.spiral.length) {
    // Enrich saved rows with the skill name (older saves didn't store it).
    spiral = existing.spiral.map((item) => ({
      ...item,
      skill_name: item.skill_name || skillNameById(item.skill_id)
    }))
  } else {
    spiral = []
    ;(plan?.spiral || []).forEach((sk) => {
      ;(sk.goals || []).forEach((g) => {
        spiral.push({
          skill_id: sk.skill_id,
          skill_name: skillNameById(sk.skill_id),
          goal_id: g.goal_id,
          goal_text: g.text,
          value: ''
        })
      })
    })
  }

  const isLkg = bundle?.level === 'LKG'

  let sequential
  if (existing && existing.sequential && existing.sequential.length) {
    sequential = existing.sequential.map((sk) => ({
      ...sk,
      skill_name: sk.skill_name || skillNameById(sk.skill_id)
    }))
  } else if (isLkg) {
    // LKG: the teacher writes a free-text observation per ADMIN-DEFINED goal
    // (from plan.sequential, authored in the Week Builder) — no fixed goals,
    // no Focus, no Yes/No. Mirrors the spiral section.
    sequential = (plan?.sequential || []).map((sp) => ({
      skill_id: sp.skill_id,
      skill_name: skillNameById(sp.skill_id),
      goals: (sp.goals || []).map((g) => ({
        goal_id: g.goal_id,
        goal_text: g.text,
        // `value` — same field the mobile app + spiral use, so a goal filled on
        // either surface lines up.
        value: ''
      }))
    }))
  } else {
    sequential = sequentialSkills.map((s) => ({
      skill_id: s.uuid,
      skill_name: s.name,
      focus_text: plan?.sequential_focus_by_skill_id?.[s.uuid] || '',
      goals: SEQUENTIAL_FIXED_GOALS.map((g, i) => ({
        goal_id: sequentialGoalId(i),
        goal_text: g,
        evaluation: ''
      }))
    }))
  }

  const exNum = (existing && existing.numeric) || {}
  const numeric = {
    focus: exNum.focus != null ? exNum.focus : plan?.numeric_focus ?? '',
    goal_id: NUMERIC_FIXED_GOAL_ID,
    // Teacher may override the goal text for their evaluation; fall back to the
    // default statement.
    goal_text: exNum.goal_text || NUMERIC_FIXED_GOAL,
    evaluation: exNum.evaluation || '',
    comment: exNum.comment || ''
  }

  return { spiral, sequential, numeric, submitted: !!existing?.submitted_at }
}

const WeeklyEvaluation = () => {
  const classes = useStyles()

  const [centres, setCentres] = useState([])
  const [centre, setCentre] = useState('')
  const [level, setLevel] = useState('NUR')
  const [dateValue, setDateValue] = useState(dayjs().format('YYYY-MM-DD'))

  const [bundle, setBundle] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [studentUuid, setStudentUuid] = useState('')
  const [spiral, setSpiral] = useState([])
  const [sequential, setSequential] = useState([])
  const [numeric, setNumeric] = useState({})
  const [submitted, setSubmitted] = useState(false)

  const { weekStart, monthKey } = useMemo(
    () => computeWeek(dateValue),
    [dateValue]
  )

  // Load centres once. Retry transient failures — on dev the ASGI server
  // serialises sync views, so a page-load burst can occasionally drop this one
  // request and leave the picker empty (the endpoint itself always returns the
  // list). A few backed-off attempts make the load resilient.
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const data = await fetchPreschoolCentres()
          const list = data?.results || (Array.isArray(data) ? data : [])
          if (cancelled) return
          if (list.length) {
            setCentres(list)
            return
          }
        } catch (e) {
          if (cancelled) return
        }
        // Transient failure or empty — wait and retry (300ms, 600ms, 900ms).
        await new Promise((r) => setTimeout(r, 300 * (attempt + 1)))
      }
      if (!cancelled)
        showToast('Failed to load centres — please reload', 'danger')
    }
    run()
    return () => {
      cancelled = true
    }
  }, [])

  // Load the weekly bundle whenever centre / level / week changes.
  const loadBundle = useCallback(async () => {
    if (!centre || !level || !weekStart) {
      setBundle(null)
      return
    }
    setLoading(true)
    setStudentUuid('')
    try {
      const data = await fetchTeacherWeeklyBundle({
        centre,
        level,
        week_start_date: weekStart
      })
      setBundle(data || null)
    } catch (e) {
      setBundle(null)
      showToast(
        e?.response?.status === 403
          ? 'Teacher login required — this page goes live once teacher sign-in is added.'
          : "Failed to load this week's roster",
        'danger'
      )
    } finally {
      setLoading(false)
    }
  }, [centre, level, weekStart])

  useEffect(() => {
    loadBundle()
  }, [loadBundle])

  // Seed the form when a student is picked (or the bundle reloads).
  useEffect(() => {
    if (!bundle || !studentUuid) {
      setSpiral([])
      setSequential([])
      setNumeric({})
      setSubmitted(false)
      return
    }
    const seeded = seedForm(bundle, studentUuid)
    setSpiral(seeded.spiral)
    setSequential(seeded.sequential)
    setNumeric(seeded.numeric)
    setSubmitted(seeded.submitted)
  }, [bundle, studentUuid])

  const roster = bundle?.roster || []
  const theme = bundle?.theme || null

  // Number Sense is only assessed from Nursery onward — Toddler and Pre-Nursery
  // don't have it (user, 2026-09). Hidden in the form + omitted from the save
  // payload for those levels.
  const showNumberSense = !['TODLR', 'PRENUR'].includes(level)

  // Per-student fill status for this week, from the loaded bundle's evaluations:
  // 'submitted' (has submitted_at), 'draft' (row exists, not submitted), or null
  // (nothing filled yet). Shown next to each name in the picker.
  const statusFor = (uuid) => {
    const ev = bundle?.existing_evaluations?.[uuid]
    if (!ev) return null
    return ev.submitted_at ? 'submitted' : 'draft'
  }

  const onSpiralValue = (idx, value) =>
    setSpiral((curr) => {
      const next = [...curr]
      next[idx] = { ...next[idx], value }
      return next
    })

  const onSeqEval = (si, gi, evaluation) =>
    setSequential((curr) => {
      const next = [...curr]
      const goals = [...next[si].goals]
      goals[gi] = { ...goals[gi], evaluation }
      next[si] = { ...next[si], goals }
      return next
    })

  const onSeqFocus = (si, focus_text) =>
    setSequential((curr) => {
      const next = [...curr]
      next[si] = { ...next[si], focus_text }
      return next
    })

  // LKG sequential goals are free-text observations (like spiral) — stored in
  // `value`, the same field the mobile app uses.
  const onSeqValue = (si, gi, value) =>
    setSequential((curr) => {
      const next = [...curr]
      const goals = [...next[si].goals]
      goals[gi] = { ...goals[gi], value }
      next[si] = { ...next[si], goals }
      return next
    })

  // Teacher can edit a literacy goal's text for THIS evaluation only (stored in
  // the eval JSON; does not change the admin's plan or other children).
  const onSeqGoalText = (si, gi, goal_text) =>
    setSequential((curr) => {
      const next = [...curr]
      const goals = [...next[si].goals]
      goals[gi] = { ...goals[gi], goal_text }
      next[si] = { ...next[si], goals }
      return next
    })

  // ---- Photos (immediate persist via the admin endpoint) ----
  const onSpiralPhoto = (idx, action, url) =>
    setSpiral((curr) => {
      const next = [...curr]
      const imgs = next[idx].image_urls || []
      next[idx] = {
        ...next[idx],
        image_urls:
          action === 'add'
            ? imgs.includes(url)
              ? imgs
              : [...imgs, url]
            : imgs.filter((u) => u !== url)
      }
      return next
    })

  const onSeqPhoto = (si, gi, action, url) =>
    setSequential((curr) => {
      const next = [...curr]
      const goals = [...next[si].goals]
      const imgs = goals[gi].image_urls || []
      goals[gi] = {
        ...goals[gi],
        image_urls:
          action === 'add'
            ? imgs.includes(url)
              ? imgs
              : [...imgs, url]
            : imgs.filter((u) => u !== url)
      }
      next[si] = { ...next[si], goals }
      return next
    })

  const addPhoto = async (goalId, file) => {
    const uploaded = await uploadFile(file, {
      uploadPreset: uploadPresets[constants.OPENBOOK],
      // Unique per upload — a fixed public_id made each new photo overwrite the
      // previous one in Cloudinary (only one ever stuck). The suffix lets an
      // observation hold multiple distinct photos.
      public_id: `weekly_eval_${studentUuid}_${goalId}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`
    })
    const url = uploaded?.secure_url || uploaded?.url
    if (!url) throw new Error('upload returned no url')
    const attach = () =>
      saveAdminEvaluationPhoto({
        student: studentUuid,
        week_start_date: weekStart,
        goal_id: goalId,
        action: 'add',
        url
      })
    try {
      await attach()
    } catch (err) {
      // 404 = no evaluation row for this child/week yet (a blank, never-saved
      // form). A teacher/super-admin can create it, so save a draft (persists
      // the seeded goal_ids) and retry the attach. This is what makes +Photo
      // work on an unfilled week instead of silently failing.
      if (err?.response?.status === 404) {
        await save(false)
        await attach()
      } else {
        throw err
      }
    }
    return url
  }

  const removePhoto = (goalId, url) =>
    saveAdminEvaluationPhoto({
      student: studentUuid,
      week_start_date: weekStart,
      goal_id: goalId,
      action: 'remove',
      url
    })

  const save = async (doSubmit) => {
    if (!studentUuid) {
      showToast('Pick a student first', 'danger')
      return
    }
    setSaving(true)
    try {
      const res = await upsertTeacherWeeklyEvaluation({
        student: studentUuid,
        week_start_date: weekStart,
        spiral,
        sequential,
        // Toddler / Pre-Nursery have no Number Sense — send an empty object so
        // no stray number-sense data is stored for them.
        numeric: showNumberSense ? numeric : {},
        submit: !!doSubmit
      })
      if (res?.submitted_at || doSubmit) setSubmitted(true)
      // Sync the just-saved evaluation back into the loaded bundle so revisiting
      // this student (or switching away and back) re-seeds the form from the
      // SAVED data — not the stale pre-save bundle. Without this the form reloads
      // blank even though the draft/submission persisted (it shows correctly
      // under Weekly Submissions but blank here — the reported teacher bug).
      if (res && studentUuid) {
        setBundle((prev) =>
          prev
            ? {
                ...prev,
                existing_evaluations: {
                  ...(prev.existing_evaluations || {}),
                  [studentUuid]: res
                }
              }
            : prev
        )
      }
      showToast(doSubmit ? 'Submitted' : 'Draft saved', 'success')
    } catch (e) {
      showToast(
        e?.response?.status === 403
          ? 'Teacher login required to save.'
          : e?.message || 'Save failed',
        'danger'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box className={classes.root}>
      {/* Top bar: centre / level / week pickers */}
      <Box className={classes.topBar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Centre</InputLabel>
          <Select
            label="Centre"
            value={centre}
            onChange={(e) => setCentre(e.target.value)}
          >
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Level</InputLabel>
          <Select
            label="Level"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          >
            {Object.entries(PRESCHOOL_LEVEL_OPTIONS).map(([code, name]) => (
              <MenuItem key={code} value={code}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Pick any date in the week"
          type="date"
          size="small"
          variant="outlined"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Typography variant="body2" className={classes.weekInfo}>
          Week of <strong>{weekStart || '—'}</strong> · {monthKey}
        </Typography>

        <Box className={classes.spacer} />
        {loading ? <CircularProgress size={22} /> : null}
      </Box>

      {theme ? (
        <Paper variant="outlined" className={classes.card}>
          <Typography className={classes.sectionTitle}>Weekly theme</Typography>
          <Typography variant="body2">
            {theme.subtheme_title || '—'}
            {theme.subtheme_topics ? ` — ${theme.subtheme_topics}` : ''}
          </Typography>
        </Paper>
      ) : null}

      {/* Student picker */}
      {bundle ? (
        <FormControl
          variant="outlined"
          size="small"
          style={{ minWidth: 260, marginBottom: 16 }}
        >
          <InputLabel>Student ({roster.length})</InputLabel>
          <Select
            label={`Student (${roster.length})`}
            value={studentUuid}
            onChange={(e) => setStudentUuid(e.target.value)}
          >
            {roster.map((s) => {
              const st = statusFor(s.student_uuid)
              return (
                <MenuItem
                  key={s.student_uuid}
                  value={s.student_uuid}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <span style={{ flex: 1 }}>
                    {s.first_name} {s.last_name}
                  </span>
                  {st ? (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.3,
                        color: st === 'submitted' ? '#2d6a4f' : '#b7791f',
                        border: `1px solid ${
                          st === 'submitted' ? '#2d6a4f' : '#b7791f'
                        }`,
                        borderRadius: 4,
                        padding: '1px 6px'
                      }}
                    >
                      {st}
                    </span>
                  ) : null}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
      ) : (
        <Typography className={classes.helpText}>
          Pick a centre, level and week to load the roster.
        </Typography>
      )}

      {/* The per-student form */}
      {bundle && studentUuid ? (
        <>
          {submitted ? (
            <Typography
              variant="body2"
              className={classes.submittedBadge}
              style={{ marginBottom: 8 }}
            >
              Already submitted — saving again updates it.
            </Typography>
          ) : null}

          {/* Observations (spiral) */}
          {spiral.length ? (
            <Paper variant="outlined" className={classes.card}>
              <Typography className={classes.sectionTitle}>
                Observations
              </Typography>
              {spiral.map((item, i) => (
                <Box key={`${item.goal_id}-${i}`} className={classes.goalBlock}>
                  {item.skill_name &&
                    (i === 0 || spiral[i - 1].skill_id !== item.skill_id) && (
                      <Typography
                        variant="subtitle1"
                        className={classes.skillName}
                      >
                        {item.skill_name}
                      </Typography>
                    )}
                  <Typography variant="body2" className={classes.goalText}>
                    {item.goal_text}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    size="small"
                    margin="dense"
                    placeholder="Observation…"
                    inputProps={{ style: { position: 'static' } }}
                    value={item.value || ''}
                    onChange={(e) => onSpiralValue(i, e.target.value)}
                  />
                  <PhotoStrip
                    imageUrls={item.image_urls}
                    onAdd={async (file) => {
                      const url = await addPhoto(item.goal_id, file)
                      onSpiralPhoto(i, 'add', url)
                    }}
                    onRemove={async (url) => {
                      await removePhoto(item.goal_id, url)
                      onSpiralPhoto(i, 'remove', url)
                    }}
                  />
                </Box>
              ))}
            </Paper>
          ) : null}

          {/* Language (sequential) */}
          {sequential.length ? (
            <Paper variant="outlined" className={classes.card}>
              <Typography className={classes.sectionTitle}>
                {sequentialStrandLabel(level)}
              </Typography>
              {sequential.map((sk, si) => (
                <Box key={sk.skill_id} className={classes.goalBlock}>
                  {sequential.length > 1 && (
                    <Typography
                      variant="subtitle1"
                      className={classes.skillName}
                    >
                      {sk.skill_name}
                    </Typography>
                  )}
                  {level === 'LKG' ? (
                    (sk.goals || []).map((g, gi) => (
                      <Box key={g.goal_id} style={{ marginTop: 8 }}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          margin="dense"
                          label="Goal"
                          value={g.goal_text || ''}
                          onChange={(e) =>
                            onSeqGoalText(si, gi, e.target.value)
                          }
                        />
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          variant="outlined"
                          size="small"
                          margin="dense"
                          placeholder="Observation…"
                          value={g.value || ''}
                          onChange={(e) => onSeqValue(si, gi, e.target.value)}
                        />
                        <PhotoStrip
                          imageUrls={g.image_urls}
                          onAdd={async (file) => {
                            const url = await addPhoto(g.goal_id, file)
                            onSeqPhoto(si, gi, 'add', url)
                          }}
                          onRemove={async (url) => {
                            await removePhoto(g.goal_id, url)
                            onSeqPhoto(si, gi, 'remove', url)
                          }}
                        />
                      </Box>
                    ))
                  ) : (
                    <>
                      <TextField
                        label="Focus"
                        fullWidth
                        variant="outlined"
                        size="small"
                        margin="dense"
                        value={sk.focus_text || ''}
                        onChange={(e) => onSeqFocus(si, e.target.value)}
                      />
                      {(sk.goals || []).map((g, gi) => (
                        <Box key={g.goal_id} style={{ marginTop: 6 }}>
                          <Box
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 12
                            }}
                          >
                            <TextField
                              variant="outlined"
                              size="small"
                              margin="dense"
                              style={{ flex: 1, marginBottom: 0 }}
                              value={g.goal_text || ''}
                              onChange={(e) =>
                                onSeqGoalText(si, gi, e.target.value)
                              }
                            />
                            <FormControl
                              variant="outlined"
                              size="small"
                              className={classes.yesNo}
                            >
                              <Select
                                value={g.evaluation || ''}
                                displayEmpty
                                onChange={(e) =>
                                  onSeqEval(si, gi, e.target.value)
                                }
                              >
                                <MenuItem value="">—</MenuItem>
                                {EVALUATION_OPTIONS.map((o) => (
                                  <MenuItem key={o} value={o}>
                                    {o}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          <PhotoStrip
                            imageUrls={g.image_urls}
                            onAdd={async (file) => {
                              const url = await addPhoto(g.goal_id, file)
                              onSeqPhoto(si, gi, 'add', url)
                            }}
                            onRemove={async (url) => {
                              await removePhoto(g.goal_id, url)
                              onSeqPhoto(si, gi, 'remove', url)
                            }}
                          />
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
              ))}
            </Paper>
          ) : null}

          {/* Numeric — only Nursery onward (hidden for Toddler / Pre-Nursery) */}
          {showNumberSense ? (
            <Paper variant="outlined" className={classes.card}>
              <Typography className={classes.sectionTitle}>
                Number Sense
              </Typography>
              <Typography variant="body2" className={classes.helpText}>
                Focus number:{' '}
                {numeric.focus === '' || numeric.focus == null
                  ? '—'
                  : numeric.focus}
              </Typography>
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12
                }}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  margin="dense"
                  style={{ flex: 1 }}
                  value={numeric.goal_text || ''}
                  onChange={(e) => {
                    // Read the value synchronously — React 16 pools synthetic
                    // events, so reading e.target inside the deferred setState
                    // updater below throws "Cannot read properties of null".
                    const value = e.target.value
                    setNumeric((curr) => ({ ...curr, goal_text: value }))
                  }}
                />
                <FormControl
                  variant="outlined"
                  size="small"
                  className={classes.yesNo}
                >
                  <Select
                    value={numeric.evaluation || ''}
                    displayEmpty
                    onChange={(e) => {
                      const value = e.target.value
                      setNumeric((curr) => ({ ...curr, evaluation: value }))
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    {EVALUATION_OPTIONS.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                size="small"
                margin="dense"
                label="Comment / remarks"
                placeholder="Add a comment for number sense…"
                inputProps={{ style: { position: 'static' } }}
                value={numeric.comment || ''}
                onChange={(e) => {
                  const value = e.target.value
                  setNumeric((curr) => ({ ...curr, comment: value }))
                }}
                style={{ marginTop: 12 }}
              />
            </Paper>
          ) : null}

          <Box style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={saving}
              onClick={() => save(false)}
            >
              Save Draft
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={() => save(true)}
              style={
                saving
                  ? undefined
                  : { backgroundColor: '#2d6a4f', color: '#fff' }
              }
            >
              Submit
            </Button>
          </Box>
        </>
      ) : null}
    </Box>
  )
}

export default WeeklyEvaluation
